import { IS_LOCAL } from '@/helpers';

const I18N = {
  ko: {
    dailyMissionDetail: {
      dateFormat: 'yy.LL.dd',
    },
  },
  en: {
    dailyMissionDetail: {
      dateFormat: 'LLL dd, yyyy',
    },
  },
};

const JanuaryStore = {
  namespaced: true,
  state() {
    return {
      /** 데일리 미션 상태 */
      dailyMissionStatus: {
        attendance: 0,
        invite: 0,
        subs: 0,
        rentalIncome: 0,
        swap: 0,
      },
      /** 간식 포크 상태 */
      forkStatus: {
        usable: 0,
        total: 0,
      },
      /** 데일리 미션 상세 */
      dailyMissionDetail: [],
      /** 누적 간식 세트 수 */
      totalTreatSets: 0,
      /** 간식 세트 보상 수령 가능 여부 */
      treatRewardAvailable: [],
      /** 이벤트 종료 후 남은 포크 수 */
      remainingForks: 0,
      /** 이벤트 종료 후 남은 포크 교환 가능 여부 */
      remainingForksAvailable: false,
    };
  },
  getters: {
    getDailyMissionDetailSum: (state) => state.dailyMissionDetail.reduce((acc, item) => acc + item.value, 0),
  },
  mutations: {
    setDailyMissionStatus(state, status) {
      state.dailyMissionStatus = status;
    },
    setForkStatus(state, status) {
      state.forkStatus = status;
    },
    setDailyMissionDetail(state, detail) {
      state.dailyMissionDetail = detail;
    },
    setTotalTreatSets(state, snack) {
      state.totalTreatSets = snack;
    },
    setTreatRewardAvailable(state, isAvailable) {
      state.treatRewardAvailable = isAvailable;
    },
    setRemainingForks(state, { fork, isAvailable }) {
      state.remainingForks = fork;
      state.remainingForksAvailable = isAvailable;
    },
  },
  actions: {
    async fetch(_, { method, url, params }) {
      const data = method === 'get' ? { params: { ...params } } : params;
      const response = await this._vm.$axios[method](`${process.env.VUE_APP_TEST_SERVER}${url}`, data);
      return response;
    },
    /**
     * 데일리 미션 상태 조회
     * @returns {Promise<{
     *   attendance: number;
     *   invite: number;
     *   subs: number;
     *   rentalIncome: number;
     *   swap: number;
     * }>}
     */
    async getDailyMissionStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          attendance: 1,
          invite: 4,
          subs: 2,
          rentalIncome: 1,
          swap: 1,
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/january/daily-mission',
      });
      return response;
    },
    /**
     * 간식 포크 상태 조회
     * @returns {Promise<{
     *   usable: number;
     *   total: number;
     * }>}
     */
    async getForkStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          usable: 4,
          total: 4,
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/january/fork',
      });
      return response;
    },
    /**
     * 이벤트 1 상태 조회
     */
    async fetchEvent1Status({ dispatch, commit }) {
      const [{ data: dailyMissionStatus }, { data: forkStatus }] = await Promise.all([
        dispatch('getDailyMissionStatus'),
        dispatch('getForkStatus'),
      ]);

      commit('setDailyMissionStatus', dailyMissionStatus);
      commit('setForkStatus', forkStatus);
      return;
    },
    /**
     * 간식 포크 상태 조회
     */
    async fetchForkStatus({ dispatch, commit }) {
      const { data: forkStatus } = await dispatch('getForkStatus');

      commit('setForkStatus', forkStatus);
      return;
    },
    /**
     * 데일리 미션 상세 조회
     * @returns {Promise<{
     *   date: string;
     *   mission: string;
     *   value: number;
     * }[]>}
     */
    async getDailyMissionDetail({ dispatch }) {
      if (IS_LOCAL) {
        return [
          {
            date: '2023-01-01T00:00:00',
            mission: '출석 체크 완료',
            value: 1,
          },
          {
            date: '2023-01-02T00:00:00',
            mission: '출석 체크 완료',
            value: 4,
          },
        ];
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/january/daily-mission/detail',
      });
      return response;
    },
    /**
     * 데일리 미션 상세 조회
     */
    async fetchDailyMissionDetail({ dispatch, commit, rootState }) {
      const { data: dailyMissionDetail } = await dispatch('getDailyMissionDetail');
      const { lang } = rootState;

      const format = I18N[lang].dailyMissionDetail.dateFormat;
      const formattedDailyMissionDetail = dailyMissionDetail.map((item) => ({
        ...item,
        date: this._vm.$tl(item.date, lang).toFormat(format),
      }));

      commit('setDailyMissionDetail', formattedDailyMissionDetail);
      return;
    },
    /**
     * 간식 보상 받기
     * @returns {Promise<number>}
     * @example 1 // 0.5, 1, 2, 2.5, 5
     */
    async postForkReward({ dispatch }) {
      if (IS_LOCAL) {
        return 1;
      }

      const response = await dispatch('fetch', {
        method: 'post',
        url: '/january/fork',
      });
      return response;
    },
    /**
     * 간식 보상 받기 후 완료 처리
     * @returns {Promise<number | Error & { message: { title: string, content: string } }>}
     * @example
     * // success
     * 1 // 0.5, 1, 2, 2.5, 5
     * // error
     * Error {
     *   "message": {
     *     "title": "간식 세트를 완성할 수 없습니다",
     *     "content": "완성하기 위한 포크가 모자랍니다. 미션을 통해 포크를 모아보세요."
     *   }
     * }
     */
    async submitForkReward({ dispatch }) {
      try {
        const { data: reward } = await dispatch('postForkReward');

        return reward;
      } catch (error) {
        if (!error.response?.data?.message) throw error;

        return error.response.data.message;
      } finally {
        dispatch('fetchForkStatus');
        dispatch('fetchTreatSetStatus');
        dispatch('fetchRemainingForks');
      }
    },
    /**
     * 누적 간식 세트 상태 조회
     * @returns {Promise<{
     *   snack: number;
     *   isAvailable: boolean[];
     * }>}
     */
    async getTreatSetStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          snack: 3,
          isAvailable: [false, false, false, false],
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/january/snack',
      });
      return response;
    },
    /**
     * 누적 간식 세트 상태 조회
     */
    async fetchTreatSetStatus({ dispatch, commit }) {
      const {
        data: { snack, isAvailable },
      } = await dispatch('getTreatSetStatus');

      commit('setTotalTreatSets', snack);
      commit('setTreatRewardAvailable', isAvailable);
      return;
    },
    /**
     * 간식 보상 받기
     */
    async postTreatReward({ dispatch }, index) {
      if (IS_LOCAL) {
        return;
      }

      await dispatch('fetch', {
        method: 'post',
        url: '/january/snack',
        params: {
          index,
        },
      });

      return;
    },
    /**
     * 간식 보상 받기
     */
    async submitTreatReward({ dispatch }, index) {
      try {
        await dispatch('postTreatReward', index);

        return true;
      } catch (error) {
        if (!error.response?.data?.message) throw error;

        return error.response.data.message;
      } finally {
        dispatch('fetchTreatSetStatus');
      }
    },
    /**
     * 이벤트 종료 후 남은 포크 수량 조회
     * @returns {Promise<{
     *   fork: number; // 남은 포크 수
     *   isAvailable: boolean; // true: 교환가능할 때
     * }>}
     */
    async getRemainingForks({ dispatch }) {
      if (IS_LOCAL) {
        return {
          fork: 4,
          isAvailable: true,
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/january/change/fork',
      });
      return response;
    },
    /**
     * 이벤트 종료 후 남은 포크 수량 업데이트
     */
    async fetchRemainingForks({ dispatch, commit }) {
      const {
        data: { fork, isAvailable },
      } = await dispatch('getRemainingForks');

      commit('setRemainingForks', { fork, isAvailable });
      return;
    },
    /**
     * 이벤트 3 교환 신청
     * @returns {Promise<boolean | {
     *   message: {
     *     title: string;
     *     content: string;
     *   };
     * }>}
     */
    async postExchange({ dispatch }) {
      if (IS_LOCAL) {
        return true;
      }

      const response = await dispatch('fetch', {
        method: 'post',
        url: '/january/change/fork',
      });
      return response;
    },
    /**
     * 이벤트 3 교환 신청
     */
    async submitExchange({ dispatch }) {
      try {
        await dispatch('postExchange');

        return true;
      } catch (error) {
        if (!error.response?.data?.message) throw error;

        return error.response.data.message;
      } finally {
        dispatch('fetchForkStatus');
        dispatch('fetchRemainingForks');
      }
    },
  },
};

export default JanuaryStore;
